import { lazy, Suspense, useMemo } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  useParams,
} from "react-router-dom";
import PrivateRoute from "./routes/PrivateRoute";
import SetupGuard from "./routes/SetupGuard";
import SubscriptionGuard from "./routes/SubscriptionGuard";
import Catalog from "./pages/catalog";
import AdminRoute from "./routes/AdminRoute";
import {
  CustomDomainRouter,
  CustomDomainUnknown,
  useCustomDomainResolution,
} from "./CustomDomainApp";

const Login = lazy(() => import("./pages/login"));
const Register = lazy(() => import("./pages/Register"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const ConfirmEmail = lazy(() => import("./pages/ConfirmEmail"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Settings = lazy(() => import("./pages/Settings"));
const Profile = lazy(() => import("./pages/Profile"));
const StoreSetup = lazy(() => import("./pages/StoreSetup"));
const Stats = lazy(() => import("./pages/stats"));
const Subscription = lazy(() => import("./pages/Subscription"));
const Legal = lazy(() => import("./pages/Legal"));
const Orders = lazy(() => import("./pages/Orders"));
const Inventory = lazy(() => import("./pages/Inventory"));
const Promotions = lazy(() => import("./pages/Promotions"));
const DeliveryPrices = lazy(() => import("./pages/DeliveryPrices"));
const ReturnExchangePolicy = lazy(() => import("./pages/ReturnExchangePolicy"));
const Wholesale = lazy(() => import("./pages/Wholesale"));
const WholesaleOrders = lazy(() => import("./pages/WholesaleOrders"));
const WholesaleStats = lazy(() => import("./pages/WholesaleStats"));
const WholesaleDealers = lazy(() => import("./pages/WholesaleDealers"));
const WholesaleLinkedStores = lazy(() => import("./pages/WholesaleLinkedStores"));
const LinkedWholesalers = lazy(() => import("./pages/LinkedWholesalers"));
const WholesaleProductMapping = lazy(() => import("./pages/WholesaleProductMapping"));
const CatalogBuilder = lazy(() => import("./pages/CatalogBuilder"));
const CatalogEditor = lazy(() => import("./pages/CatalogEditor"));
const CatalogStudio = lazy(() => import("./pages/CatalogStudio"));
const Landing = lazy(() => import("./pages/Landing"));
const AdminIndex = lazy(() => import("./pages/admin/AdminIndex"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminAlerts = lazy(() => import("./pages/admin/AdminAlerts"));
const AdminInsights = lazy(() => import("./pages/admin/AdminInsights"));
const AdminCreateAccount = lazy(() => import("./pages/admin/AdminCreateAccount"));
const AdminLinkAccount = lazy(() => import("./pages/admin/AdminLinkAccount"));
const AdminUserDetail = lazy(() => import("./pages/admin/AdminUserDetail"));
const AdminCatalogStudio = lazy(() => import("./pages/admin/AdminCatalogStudio"));

function CatalogWholesaleOrdersRedirect() {
  const { storeKey } = useParams();
  return <Navigate to={`/catalog/${encodeURIComponent(storeKey || "")}`} replace />;
}

function RootIndex() {
  const token =
    typeof window !== "undefined" ? window.localStorage.getItem("token") : null;
  return token ? <Navigate to="/dashboard" replace /> : <Landing />;
}

const platformRouter = createBrowserRouter(
  [
    { path: "/", element: <RootIndex /> },
    { path: "/login", element: <Login /> },
    { path: "/register", element: <Register /> },
    { path: "/auth/callback", element: <AuthCallback /> },
    { path: "/confirm-email", element: <ConfirmEmail /> },
    { path: "/reset-password", element: <ResetPassword /> },
    { path: "/legal", element: <Legal /> },
    { path: "/admin", element: <AdminIndex /> },
    {
      element: <AdminRoute />,
      children: [
        {
          path: "/admin/catalog-studio/:userId/:storeKey",
          element: <AdminCatalogStudio />,
        },
        {
          element: <AdminLayout />,
          children: [
            { path: "/admin/dashboard", element: <AdminDashboard /> },
            { path: "/admin/alerts", element: <AdminAlerts /> },
            { path: "/admin/insights", element: <AdminInsights /> },
            { path: "/admin/create-account", element: <AdminCreateAccount /> },
            { path: "/admin/link-account", element: <AdminLinkAccount /> },
            { path: "/admin/users/:userId", element: <AdminUserDetail /> },
          ],
        },
      ],
    },
    { path: "/catalog/:storeKey/menu", element: <Catalog menuOnly /> },
    { path: "/catalog/:storeKey/menu/:productId", element: <Catalog menuOnly /> },
    { path: "/catalog/:storeKey", element: <Catalog /> },
    { path: "/catalog/:storeKey/:productId", element: <Catalog /> },
    { path: "/catalog/:storeKey/wholesale/orders", element: <CatalogWholesaleOrdersRedirect /> },
    {
      element: <PrivateRoute />,
      children: [
        {
          element: <SetupGuard />,
          children: [
            {
              element: <SubscriptionGuard />,
              children: [
                { path: "/setup", element: <StoreSetup /> },
                { path: "/dashboard", element: <Dashboard /> },
                { path: "/stats", element: <Stats /> },
                { path: "/settings", element: <Settings /> },
                { path: "/catalog-builder", element: <CatalogBuilder /> },
                { path: "/catalog-editor", element: <CatalogEditor /> },
                { path: "/catalog-studio", element: <CatalogStudio /> },
                { path: "/catalog-studio/:storeKey", element: <CatalogStudio /> },
                { path: "/profile", element: <Profile /> },
                { path: "/subscription", element: <Subscription /> },
                { path: "/layouts", element: <Settings /> },
                { path: "/orders", element: <Orders /> },
                { path: "/linked-wholesalers", element: <LinkedWholesalers /> },
                { path: "/linked-wholesalers/:linkId", element: <LinkedWholesalers /> },
                {
                  path: "/linked-wholesalers/:linkId/mapping",
                  element: <WholesaleProductMapping />,
                },
                { path: "/delivery-prices", element: <DeliveryPrices /> },
                { path: "/return-exchange-policy", element: <ReturnExchangePolicy /> },
                { path: "/inventory", element: <Inventory /> },
                { path: "/promotions", element: <Promotions /> },
                { path: "/wholesale", element: <Wholesale /> },
                { path: "/wholesale/orders", element: <WholesaleOrders /> },
                { path: "/wholesale/stats", element: <WholesaleStats /> },
                { path: "/wholesale/dealers", element: <WholesaleDealers /> },
                { path: "/wholesale/linked-stores", element: <WholesaleLinkedStores /> },
                { path: "/wholesale/linked-stores/:linkId", element: <WholesaleLinkedStores /> },
              ],
            },
          ],
        },
      ],
    },
  ],
  {
    future: {
      v7_startTransition: true,
    },
  }
);

function App() {
  const domainState = useCustomDomainResolution();

  const platformRouterMemo = useMemo(() => platformRouter, []);

  if (domainState.status === "loading") {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          fontFamily: "system-ui, sans-serif",
          color: "#64748b",
        }}
      >
        Loading…
      </div>
    );
  }

  if (domainState.status === "matched" && domainState.catalogKey) {
    return <CustomDomainRouter catalogKey={domainState.catalogKey} />;
  }

  if (domainState.status === "unknown") {
    return <CustomDomainUnknown />;
  }

  return (
    <Suspense fallback={null}>
      <RouterProvider router={platformRouterMemo} />
    </Suspense>
  );
}

export default App;
